import type {
  FormSchemaVersion,
  BespokeTemplateResponse,
  BespokeTemplateStatus,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import {
  createBespokeTemplateBespokeTemplatesPost,
  listBespokeTemplatesBespokeTemplatesGet,
  updateBespokeTemplateBespokeTemplatesTemplateIdPatch,
} from '../../services/api/sdk.gen';
import type { FormTreeQuestion } from './formSchemaTypes';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import {
  bespokeTemplateMatchesPin,
  layoutFieldQidSignatureHexFromQuestions,
  requireSavedBespokeTemplate,
  schemaVersionLabel,
  validateLayoutJsonForSection,
  type FormSchemaVersionPin,
} from './formSchemaUtils';

export type MatchingLiveLayoutSource = {
  template: BespokeTemplateResponse;
  schemaVersion: FormSchemaVersion;
  sectionName: string;
  rogerDocFormType?: RogerDocFormType;
  formTypeDisplayLabel?: string;
};

/**
 * Validate a section layout, then PATCH it onto an existing bespoke template.
 *
 * Single source of truth for the "save a layout to a live bespoke template"
 * contract shared by the matching-copy and section-port apply flows: it fails
 * closed (throws before any PATCH when the layout fails section validation),
 * and returns the saved row so callers can patch local state without
 * refetching the full template list. `status` is only sent when provided —
 * omit it to leave the template's current status untouched.
 */
export async function patchBespokeTemplateLayout(options: {
  templateId: string;
  layoutJson: string;
  questions: FormTreeQuestion[];
  name: string;
  status?: BespokeTemplateStatus;
  /**
   * Persist even when section validation fails. Only for an explicit
   * "copy anyway" override in a caller's UI — default remains fail-closed.
   * Callers still own deciding *when* to set this; this function only owns
   * the save mechanics.
   */
  skipValidation?: boolean;
}): Promise<BespokeTemplateResponse> {
  const {
    templateId,
    layoutJson,
    questions,
    name,
    status,
    skipValidation = false,
  } = options;
  let layout: unknown[];
  if (skipValidation) {
    layout = JSON.parse(layoutJson) as unknown[];
  } else {
    const validation = validateLayoutJsonForSection(layoutJson, questions);
    if (!validation.ok) {
      throw new Error(validation.error);
    }
    layout = validation.layout;
  }
  const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
    path: { template_id: templateId },
    body: {
      layout_json: layout,
      name,
      ...(status ? { status } : {}),
    },
    throwOnError: true,
  });
  return requireSavedBespokeTemplate(res.data);
}

export function formatMatchingCopyAttribution(
  match: MatchingLiveLayoutSource
): string {
  return [
    schemaVersionLabel(match.schemaVersion),
    formatSectionDropdownLabel(match.sectionName),
    match.template.name,
  ].join(' · ');
}

/**
 * Live bespoke templates whose layout answer-control QID fingerprint matches
 * this section's schema coverage, excluding the destination slot (full pin).
 */
export async function fetchMatchingLiveLayoutSourcesForSection(options: {
  destinationSectionName: string;
  destinationFormSchemaVersionPin: FormSchemaVersionPin;
  questions: FormTreeQuestion[];
}): Promise<MatchingLiveLayoutSource[]> {
  const { destinationSectionName, destinationFormSchemaVersionPin, questions } =
    options;

  if (!questions.length) {
    return [];
  }

  const sig = await layoutFieldQidSignatureHexFromQuestions(questions);
  const res = await listBespokeTemplatesBespokeTemplatesGet({
    query: {
      layout_field_qid_signature: sig,
      status: 'live',
    },
    throwOnError: true,
  });

  const candidates = res.data ?? [];
  const filtered = candidates.filter(t => {
    if (!t.layout_json) return false;
    const isDestinationSlot =
      bespokeTemplateMatchesPin(t, destinationFormSchemaVersionPin) &&
      t.section_name === destinationSectionName;
    return !isDestinationSlot;
  });

  const matches: MatchingLiveLayoutSource[] = filtered.map(tpl => ({
    template: tpl,
    schemaVersion: {
      name: tpl.schema_version_name,
      version: tpl.form_schema_version,
      form_type: tpl.form_type,
    },
    sectionName: tpl.section_name,
    rogerDocFormType: tpl.form_type as RogerDocFormType,
  }));

  const seen = new Set<string>();
  const deduped = matches.filter(m => {
    if (seen.has(m.template.id)) return false;
    seen.add(m.template.id);
    return true;
  });

  deduped.sort((a, b) => {
    const byVer = schemaVersionLabel(a.schemaVersion).localeCompare(
      schemaVersionLabel(b.schemaVersion),
      undefined,
      { sensitivity: 'base' }
    );
    if (byVer !== 0) return byVer;
    const bySec = formatSectionDropdownLabel(a.sectionName).localeCompare(
      formatSectionDropdownLabel(b.sectionName),
      undefined,
      { sensitivity: 'base' }
    );
    if (bySec !== 0) return bySec;
    return a.template.name.localeCompare(b.template.name, undefined, {
      sensitivity: 'base',
    });
  });

  return deduped;
}

/**
 * Returns the saved row so callers can patch local state directly instead of
 * refetching the (unscoped, potentially large) full template list.
 */
export async function applyMatchingLayoutCopyFromSource(options: {
  match: MatchingLiveLayoutSource;
  destinationSectionName: string;
  questions: FormTreeQuestion[];
  selectedRogerDocFormType: RogerDocFormType;
  selectedVersion: FormSchemaVersion;
  existingTemplateBySection: Map<string, BespokeTemplateResponse>;
}): Promise<BespokeTemplateResponse> {
  const {
    match,
    destinationSectionName,
    questions,
    selectedRogerDocFormType,
    selectedVersion,
    existingTemplateBySection,
  } = options;

  if (!match.template.layout_json) {
    throw new Error('Source template has no layout.');
  }

  if (!selectedVersion.version) {
    throw new Error(
      'The selected schema is missing a version identifier and cannot be saved.'
    );
  }

  const layoutJson = JSON.stringify(match.template.layout_json);
  const attribution = formatMatchingCopyAttribution(match);
  const displayName = formatSectionDropdownLabel(destinationSectionName);
  const existing = existingTemplateBySection.get(destinationSectionName);

  if (existing) {
    return patchBespokeTemplateLayout({
      templateId: existing.id,
      layoutJson,
      questions,
      name: existing.name || `${displayName} layout`,
      status: 'live',
    });
  }

  const validation = validateLayoutJsonForSection(layoutJson, questions);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const res = await createBespokeTemplateBespokeTemplatesPost({
    body: {
      form_type: selectedRogerDocFormType,
      schema_version_name: selectedVersion.name,
      form_schema_version: selectedVersion.version,
      section_name: destinationSectionName,
      layout_json: validation.layout,
      name: `${displayName} layout`,
      description: attribution,
      status: 'live',
    },
    throwOnError: true,
  });
  return requireSavedBespokeTemplate(res.data);
}
